import { PushpinOutlined, PushpinFilled } from '@ant-design/icons';
import { Popover } from 'antd';
import React from 'react';
import { useQueryClient } from 'react-query';
import { IBook } from '../../../section/Common/types/types';
import { useActions } from '../../../context/Actions.context';
import { GAEventIdentifier, GAEventName } from '../../../section/Common/constants';
import { useUpdateTitle } from '../../../core/modules/book/hooks/useUpdateTitle.query';
import showNotification from '../../../section/Common/utils/showNotification';
import { SOURCE } from '../../../core/constants';

interface IProps {
  book: IBook | Record<string, any>;
  id: string;
  label?: string;
}

const PinTitle: React.FC<IProps> = ({ book, id, label = 'Title' }) => {
  const { source, setActionLoading } = useActions();
  const queryClient = useQueryClient();
  const isPinned = Boolean(book?.is_pinned_watchlist);

  const { mutateAsync } = useUpdateTitle(id);

  const handleTogglePin = (e: React.MouseEvent) => {
    e.stopPropagation();

    globalThis.gtag('event', GAEventName.click, {
      identifier: isPinned ? GAEventIdentifier.unpinTitle : GAEventIdentifier.pinTitle,
      value: book.ASIN,
      book_name: book.Title
    });

    setActionLoading(true);
    const sourceParams = {
      [SOURCE.bookstat]: { amazonasin: book.ASIN },
      [SOURCE.ao3]: { work_id: book.work_id },
      [SOURCE['royal-road']]: { fiction_id: book['Fiction Id'] }
    };

    const body = {
      is_pinned_watchlist: isPinned ? 0 : 1,
      pin_action: isPinned ? 'unpin' : 'pin'
    };

    const promise = mutateAsync({ params: sourceParams[source?.['source']], body });

    showNotification(
      promise,
      `The ${label} was successfully ${isPinned ? 'unpinned' : 'pinned'}.`,
      `The ${label} has not been ${isPinned ? 'unpinned' : 'pinned'}.`,
      typeof book?.Title === 'string' ? book.Title : undefined
    );

    promise.then(() => {
      queryClient.refetchQueries(`get-row-count${id}`);
      queryClient.refetchQueries(`get-${id}`);
    });

    promise.finally(() => {
      setActionLoading(false);
    });
  };

  return (
    <Popover
      placement='topLeft'
      content={
        <div className='popover-content'>
          {isPinned ? `Unpin ${label}` : `Pin ${label} to top`}
        </div>
      }
      trigger='hover'
    >
      {isPinned ? (
        <PushpinFilled
          data-testid='action-unpin-item'
          onClick={handleTogglePin}
          className='actions-icons actions-icons-pin pinned'
        />
      ) : (
        <PushpinOutlined
          data-testid='action-pin-item'
          onClick={handleTogglePin}
          className='actions-icons actions-icons-pin'
        />
      )}
    </Popover>
  );
};

export default React.memo(PinTitle);